/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import * as $ from "jquery";
import {UiPanelHeaderFieldConfig} from "../generated/UiPanelHeaderFieldConfig";
import {UiField} from "./formfield/UiField";
import {TeamAppsEvent} from "./util/TeamAppsEvent";
import {UiToolbar} from "./UiToolbar";
import {UiComponentConfig} from "../generated/UiComponentConfig";
import {UiItemViewConfig} from "../generated/UiItemViewConfig";
import {UiItemView} from "./UiItemView";
import {UiToolbarConfig} from "../generated/UiToolbarConfig";
import {UiToolButton} from "./micro-components/UiToolButton";
import {UiComponent} from "./UiComponent";
import {UiDropDown} from "./micro-components/UiDropDown";
import {TeamAppsUiContext} from "./TeamAppsUiContext";
import {executeWhenAttached} from "./util/ExecuteWhenAttached";
import {
	UiPanel_HeaderComponentMinimizationPolicy,
	UiPanel_ToolButtonClickedEvent,
	UiPanel_ToolButtonDropDownItemClickedEvent,
	UiPanelCommandHandler,
	UiPanelConfig,
	UiPanelEventSource
} from "../generated/UiPanelConfig";
import {UiSingleRecordFieldContainer_FieldEventEvent, UiSingleRecordFieldContainer_FieldValueChangedEvent} from "../generated/UiSingleRecordFieldContainerConfig";
import {createUiToolButtonConfig, UiToolButtonConfig} from "../generated/UiToolButtonConfig";
import {TeamAppsUiComponentRegistry} from "./TeamAppsUiComponentRegistry";
import {EventFactory} from "../generated/EventFactory";
import {SubEventRegistrator} from "../generated/SubEventRegistrator";
import {UiSubEvent} from "../generated/UiSubEvent";
import {SubCommandExecutor} from "../generated/SubCommandExecutor";
import {createSimpleTextCellTemplate} from "./Common";
import {bind} from "./util/Bind";
import {createUiColorCssString} from "./util/CssFormatUtil";
import {StaticIcons} from "./util/StaticIcons";

/**
 * @author Yann Massard (yamass@gmail.com)
 */
interface HeaderField {
	config: UiPanelHeaderFieldConfig;
	field: UiField;
	$wrapper: JQuery;
	$iconAndFieldWrapper: JQuery;
	$fieldWrapper: JQuery;
	$icon: JQuery;
	minimizedWidth?: number;
	minExpandedWidthWithIcon?: number;
	minExpandedWidth?: number;
}

export enum SpecialToolButtonType {
	MINIMIZE = "minimize",
	RESTORE = "restore",
	MAXIMIZE = "maximize",
	CLOSE = "close"
}

const orderedDefaultSpecialButtonIds = ["minimize", "restore", "maximize", "close"];

export class UiPanel extends UiComponent<UiPanelConfig> implements UiPanelCommandHandler, UiPanelEventSource {
	public readonly onFieldValueChanged: TeamAppsEvent<UiSingleRecordFieldContainer_FieldValueChangedEvent> = new TeamAppsEvent<UiSingleRecordFieldContainer_FieldValueChangedEvent>(this);
	public readonly onFieldEvent: TeamAppsEvent<UiSingleRecordFieldContainer_FieldEventEvent> = new TeamAppsEvent<UiSingleRecordFieldContainer_FieldEventEvent>(this);
	public readonly onToolButtonClicked: TeamAppsEvent<UiPanel_ToolButtonClickedEvent> = new TeamAppsEvent<UiPanel_ToolButtonClickedEvent>(this);
	public readonly onToolButtonDropDownItemClicked: TeamAppsEvent<UiPanel_ToolButtonDropDownItemClickedEvent> = new TeamAppsEvent<UiPanel_ToolButtonDropDownItemClickedEvent>(this);

	public readonly onSpecialToolButtonPressed: TeamAppsEvent<string> = new TeamAppsEvent(this);

	private $panel: JQuery;
	private $heading: JQuery;
	private $parentDomElement: JQuery; // When maximizing, this component is taken out of the DOM. When minimizing, it has to be reattached to this parent element.
	private $toolbarContainer: JQuery;
	private $bodyContainer: JQuery;
	private $leftComponentWrapper: JQuery;
	private $headingSpacer: JQuery;
	private $rightComponentWrapper: JQuery;
	private $buttonContainer: JQuery;
	private $specialButtonContainer: JQuery;
	private $icon: JQuery;
	private $title: JQuery;

	private contentComponent: UiComponent<UiComponentConfig>;
	private headerFieldsByName: { [index: string]: HeaderField } = {};
	private leftHeaderField: HeaderField;
	private rightHeaderField: HeaderField;
	private leftComponentFirstMinimized: boolean;
	private toolbar: UiToolbar;
	private icon: string;
	private title: string;

	private titleNaturalWidth: number;
	private toolButtons: { [id: string]: UiToolButton } = {};
	private dropDown: UiDropDown;
	private specialButtonsById: { [id: string]: UiToolButton } = {};

	constructor(config: UiPanelConfig, context: TeamAppsUiContext) {
		super(config, context);
		this.$panel = $(`<div id="${config.id}" class="UiPanel panel teamapps-blurredBackgroundImage">
                <div class="panel-heading">
                    <div class="panel-icon"/>
                    <div class="panel-title" style="color: ${createUiColorCssString(config.headerFontColor)}"/>
                    <div class="panel-component-wrapper panel-left-component-wrapper"/>
                    <div class="panel-heading-spacer"/>
                    <div class="panel-component-wrapper panel-right-component-wrapper"/>
                    <div class="panel-heading-buttons"/>
                    <div class="panel-heading-special-buttons hidden"/>
                </div>
                <div class="toolbar-container"></div>
                <div class="panel-body">
                  <div class="body-container scroll-container" style="padding: ${config.padding}px"></div>
                </div>
            </div>`);

		this.$toolbarContainer = this.$panel.find('.toolbar-container');
		this.$bodyContainer = this.$panel.find('.body-container');
		this.$heading = this.$panel.find('>.panel-heading');
		this.$icon = this.$heading.find('>.panel-icon');
		this.$title = this.$heading.find('>.panel-title');
		this.$leftComponentWrapper = this.$heading.find('>.panel-left-component-wrapper');
		this.$headingSpacer = this.$heading.find('>.panel-heading-spacer');
		this.$rightComponentWrapper = this.$heading.find('>.panel-right-component-wrapper');
		this.$buttonContainer = this.$heading.find('>.panel-heading-buttons');
		this.$specialButtonContainer = this.$heading.find('>.panel-heading-special-buttons');

		this.setIcon(config.icon);
		this.setTitle(config.title);
		this.setLeftHeaderField(config.leftHeaderField);
		this.setRightHeaderField(config.rightHeaderField);
		if (config.initialHeaderFieldValues) {
			Object.keys(config.initialHeaderFieldValues).forEach(fieldName => this.setFieldValue(fieldName, config.initialHeaderFieldValues[fieldName]));
		}
		this.setToolButtons(config.toolButtons);

		if (config.hideTitleBar) {
			this.$heading.addClass('hidden');
			this.$panel.addClass("empty-heading");
		} else {
			this.$heading.removeClass('hidden');
			this.$panel.removeClass("empty-heading");
		}

		if (config.headerBackgroundColor) {
			this.$heading.css("background-color", createUiColorCssString(config.headerBackgroundColor));
		}
		if (config.bodyBackgroundColor) {
			this.$panel.find('.panel-body').css("background-color", createUiColorCssString(config.bodyBackgroundColor));
		}
		this.setToolbar(config.toolbar);
		if (config.content) {
			this.setContent(config.content);
		}
		this.leftComponentFirstMinimized = this._config.headerComponentMinimizationPolicy == UiPanel_HeaderComponentMinimizationPolicy.LEFT_COMPONENT_FIRST;

		this.dropDown = new UiDropDown();
		this.dropDown.onComponentRemoved = (component: UiComponent<UiComponentConfig>) => component.destroy();
		this.dropDown.onClose = (component?: UiComponent<UiComponentConfig>) => component && component.destroy();

		this.onSpecialToolButtonPressed.addListener(this.handleSpecialToolButtonPressed);
		this.setMaximizable(config.maximizable);
	}

	public setDraggable(draggable: boolean) {
		this.$heading.prop("draggable", draggable);
	}

	@bind
	private handleSpecialToolButtonPressed(buttonId: string) {
		if (this._config.maximizable && buttonId === SpecialToolButtonType.MAXIMIZE) {
			this.setMaximized(true);
			this.removeSpecialButton(SpecialToolButtonType.MAXIMIZE);
			this.addDefaultSpecialButton(SpecialToolButtonType.RESTORE);
		} else if (this._config.maximizable && buttonId === SpecialToolButtonType.RESTORE) {
			this.setMaximized(false);
			this.removeSpecialButton(SpecialToolButtonType.RESTORE);
			this.addDefaultSpecialButton(SpecialToolButtonType.MAXIMIZE);
		}
	}

	public setMaximizable(maximizable: boolean) {
		if (maximizable) {
			this.addDefaultSpecialButton(SpecialToolButtonType.MAXIMIZE);
		} else {
			this.removeSpecialButton(SpecialToolButtonType.MAXIMIZE);
		}
	}

	public setToolButtons(toolButtons: UiToolButtonConfig[]) {
		this.$buttonContainer.empty();
		this.toolButtons = {};
		toolButtons && toolButtons.forEach(buttonConfig => {
			let toolButton = new UiToolButton(buttonConfig, () => {
				this.onToolButtonClicked.fire(EventFactory.createUiPanel_ToolButtonClickedEvent(this._config.id, buttonConfig.id));
			}, this._context);
			toolButton.getMainDomElement().appendTo(this.$buttonContainer);
			this.toolButtons[buttonConfig.id] = toolButton;
		});
		this.relayoutHeader();
	}

	public setSpecialToolButtons(toolButtons: UiToolButtonConfig[]) {
		this.$specialButtonContainer.empty();
		if (toolButtons && toolButtons.length > 0) {
			toolButtons.forEach(buttonConfig => {
				this.addSpecialButton(buttonConfig);
			});
		} else {
			Object.keys(this.specialButtonsById).forEach(buttonId => this.removeSpecialButton(buttonId));
		}
	}

	public addSpecialButton(buttonConfig: UiToolButtonConfig) {
		if (this.specialButtonsById[buttonConfig.id] != null) {
			this.removeSpecialButton(buttonConfig.id);
		}
		this.$specialButtonContainer.removeClass("hidden");
		let toolButton = new UiToolButton(buttonConfig, () => this.onSpecialToolButtonPressed.fire(buttonConfig.id), this._context);
		this.specialButtonsById[buttonConfig.id] = toolButton;
		if (orderedDefaultSpecialButtonIds.indexOf(buttonConfig.id) === -1 || this.$specialButtonContainer[0].children.length === 0) {
			toolButton.getMainDomElement().prependTo(this.$specialButtonContainer);
		} else {
			let index = Object.keys(this.specialButtonsById)
				.map(id => this.specialButtonsById[id].getId())
				.sort((a, b) => orderedDefaultSpecialButtonIds.indexOf(a) - orderedDefaultSpecialButtonIds.indexOf(b))
				.indexOf(buttonConfig.id);
			if (index >= this.$specialButtonContainer[0].childNodes.length) {
				toolButton.getMainDomElement().appendTo(this.$specialButtonContainer);
			} else {
				toolButton.getMainDomElement().insertBefore(this.$specialButtonContainer[0].children[index]);
			}
		}
		this.relayoutHeader();
	}

	public removeSpecialButton(buttonId: string) {
		let uiToolButton = this.specialButtonsById[buttonId];
		if (uiToolButton) {
			uiToolButton.getMainDomElement().remove();
			delete this.specialButtonsById[buttonId];
			if (Object.keys(this.specialButtonsById).length === 0) {
				this.$specialButtonContainer.addClass("hidden");
			}
		}
	}

	public addDefaultSpecialButton(buttonType: SpecialToolButtonType) {
		this.addSpecialButton(UiPanel.createSpecialToolButtonConfig(buttonType, this._context.config.optimizedForTouch ? 16 : 12));
	}

	public showToolButtonDropDown(toolButtonId: string, itemViewConfig: UiItemViewConfig, dropDownWidth: number) {
		let toolButton = this.toolButtons[toolButtonId];
		if (!toolButton) {
			this.logger.error(`ToolButton with id ${toolButtonId} does not exist on UiPanel ${this._config.id}!`);
		}
		let itemView = new UiItemView(itemViewConfig, this._context);
		itemView.onItemClicked.addListener(eventObject => {
			this.dropDown.close();
			this.onToolButtonDropDownItemClicked.fire(EventFactory.createUiPanel_ToolButtonDropDownItemClickedEvent(this.getId(), toolButtonId, eventObject.itemId));
		});
		this.dropDown.setContent(itemView);
		this.dropDown.open(toolButton.getMainDomElement(), {
			width: dropDownWidth
		});
	}

	public getMainDomElement(): JQuery {
		return this.$panel;
	}

	public setContent(content: UiComponentConfig) {
		this.setContentComponent(content != null ? this._context.createComponent(content) : null);
	}

	public setContentComponent(content: UiComponent) {
		if (this.contentComponent) {
			this.contentComponent.destroy();
		}
		this.$bodyContainer.empty();
		this.contentComponent = content;
		if (content != null) {
			this.contentComponent.getMainDomElement().appendTo(this.$bodyContainer);
			this.contentComponent.attachedToDom = this.attachedToDom;
		}
	}

	protected onAttachedToDom() {
		if (this.toolbar) this.toolbar.attachedToDom = true;
		if (this.contentComponent) this.contentComponent.attachedToDom = true;
		this.reLayout();
	}

	@executeWhenAttached(true)
	private calculateFieldWrapperSizes() {
		Object.keys(this.headerFieldsByName).forEach(fieldName => {
			let headerField = this.headerFieldsByName[fieldName];
			if (!headerField.minimizedWidth || !headerField.minExpandedWidth || !headerField.minExpandedWidthWithIcon) {
				headerField.$fieldWrapper.css("transition", "none");
				this.setHeaderFieldMinimized(headerField, true);
				this.$heading.addClass("has-minimized-header-component");
				headerField.minimizedWidth = headerField.$iconAndFieldWrapper.outerWidth(true);
				this.setHeaderFieldMinimized(headerField, false);
				headerField.minExpandedWidthWithIcon = headerField.$iconAndFieldWrapper.outerWidth(true) - headerField.$fieldWrapper[0].offsetWidth + headerField.config.minWidth;
				this.$heading.removeClass("has-minimized-header-component");
				headerField.minExpandedWidth = headerField.$iconAndFieldWrapper.outerWidth(true) - headerField.$fieldWrapper[0].offsetWidth + headerField.config.minWidth;
				headerField.$fieldWrapper.css("transition", "");
			}
		});
	}

	private setHeaderFieldMinimized(headerField: HeaderField, minimized: boolean) {
		headerField.$wrapper.toggleClass("minimized", minimized);
	}

	public setLeftHeaderField(headerFieldConfig: UiPanelHeaderFieldConfig) {
		this.leftHeaderField = this.setHeaderField(headerFieldConfig, this.$leftComponentWrapper, true);
		this.calculateFieldWrapperSizes();
		this.relayoutHeader();
	}

	public setRightHeaderField(headerFieldConfig: UiPanelHeaderFieldConfig) {
		this.rightHeaderField = this.setHeaderField(headerFieldConfig, this.$rightComponentWrapper, false);
		this.calculateFieldWrapperSizes();
		this.relayoutHeader();
	}

	private setHeaderField(headerFieldConfig: UiPanelHeaderFieldConfig, $componentWrapper: JQuery | any, isLeft: boolean): HeaderField {
		if (headerFieldConfig && this.headerFieldsByName[headerFieldConfig.field.fieldName]) {
			this.removeField(headerFieldConfig.field.fieldName);
		}

		$componentWrapper.empty().hide();
		if (headerFieldConfig) {
			let iconPath = this._context.getIconPath(headerFieldConfig.icon, 16);
			let $iconAndFieldWrapper = $(`<div class="icon-and-field-wrapper">
                    <div class="icon img img-16" style="background-image: ${iconPath ? 'url(' + iconPath + ')' : 'none'}"></div>
                    <div class="field-wrapper"></div>
                </div>`);
			let $icon = $iconAndFieldWrapper.find('>.icon');
			$icon.click(() => {
				this.leftComponentFirstMinimized = !isLeft;
				this.relayoutHeader();
			});
			let $fieldWrapper = $iconAndFieldWrapper.find('>.field-wrapper');
			let field = <UiField> new (TeamAppsUiComponentRegistry.getFieldClassForName(headerFieldConfig.field._type))(headerFieldConfig.field, $fieldWrapper[0], this._context);
			field.onValueChanged.addListener(eventObject => {
				if (!this._context.executingCommand) {
					this.onFieldValueChanged.fire(EventFactory.createUiSingleRecordFieldContainer_FieldValueChangedEvent(this.getId(), headerFieldConfig.field.fieldName, eventObject.value));
				}
			});
			field.onVisibilityChanged.addListener(visible => {
				this.relayoutHeader();
			});
			SubEventRegistrator.registerForUiFieldSubEvents(field, (eventObject: UiSubEvent) => {
				this.onFieldEvent.fire(EventFactory.createUiSingleRecordFieldContainer_FieldEventEvent(this.getId(), headerFieldConfig.field.fieldName, eventObject))
			});
			let headerField: HeaderField = {
				config: headerFieldConfig,
				field,
				$wrapper: $componentWrapper,
				$iconAndFieldWrapper,
				$icon: $icon,
				$fieldWrapper
			};
			this.headerFieldsByName[headerFieldConfig.field.fieldName] = headerField;
			$componentWrapper.append($iconAndFieldWrapper).show();
			return headerField;
		} else {
			return null;
		}
	};

	public removeField(fieldName: string) {
		let headerField = this.headerFieldsByName[fieldName];
		if (headerField) {
			headerField.$iconAndFieldWrapper.remove();
			headerField.field.destroy();
			delete this.headerFieldsByName[fieldName];
		}
	}

	public setFieldValue(fieldName: string, value: any): void {
		const field = this.headerFieldsByName[fieldName];
		if (field) {
			field.field.setCommittedValue(value);
		}
	}

	public setIcon(icon: string) {
		this.icon = icon;
		if (icon) {
			this.$icon.empty();
			this.$icon.append('<div class="img img-16" style="background-image: url(' + this._context.getIconPath(icon, 16) + ')"/>');
		} else {
			this.$icon.hide();
		}
	}

	public setTitle(title: string) {
		this.title = title;
		if (title) {
			this.$title.text(title);
			this.$title.show();
		} else {
			this.$title.hide();
		}
		this.recalculateTitleNaturalWidth();
		this.relayoutHeader();
	}

	private recalculateTitleNaturalWidth() {
		this.$title.css({
			position: "absolute",
			display: "inline-block"
		}); // TODO
		this.titleNaturalWidth = this.$title[0].offsetWidth;
		this.$title.css({
			position: "",
			display: ""
		});
	};

	public setToolbar(toolbarConfig: UiToolbarConfig) {
		if (this.toolbar != null) {
			this.toolbar.destroy();
			this.toolbar.getMainDomElement().remove(); // TODO this should be part of the destroy method...
			this.toolbar = null;
		}
		if (toolbarConfig) {
			this.toolbar = <UiToolbar>this._context.createComponent(toolbarConfig);
			this.toolbar.getMainDomElement().appendTo(this.$toolbarContainer);
			this.toolbar.onEmptyStateChanged.addListener(() => this.updateToolbarVisibility())
		}
		this.updateToolbarVisibility();
	}

	private updateToolbarVisibility() {
		this.$toolbarContainer.toggleClass('hidden', this.toolbar == null || this.toolbar.empty);
	}

	public setMaximized(maximized: boolean) {
		if (maximized) {
			this.maximize();
		} else {
			this.restore();
		}
	}

	public maximize(): void {
		this.$parentDomElement = this.getMainDomElement().parent();
		const scrollTop = $(document).scrollTop();
		const scrollLeft = $(document).scrollLeft();
		const offset = this.$panel.offset();
		this.$panel.css({
			position: 'fixed',
			top: (offset.top - scrollTop) + "px",
			left: (offset.left - scrollLeft) + "px",
			width: this.$panel.width(),
			height: this.$panel.height(),
			"z-index": 1000000
		}).appendTo(
			$('body')
		).animate({
			top: "5px",
			left: "5px",
			width: ($(window).width() - 10),
			height: ($(window).height() - 10)
		}, 200, 'swing', () => {
			this.$panel.css({
				width: "calc(100% - 10px)",
				height: "calc(100% - 10px)"
			});
			this.$panel.addClass("panel-maximized");

			this.contentComponent && this.contentComponent.reLayout();
			this.toolbar && this.toolbar.reLayout();

		});
	}

	public restore(): void {
		this.$panel.removeClass("panel-maximized");
		this.$panel.appendTo(this.$parentDomElement);
		this.$panel.css({
			position: '',
			top: '',
			left: '',
			width: '',
			height: '',
			"z-index": ''
		});

		this.contentComponent && this.contentComponent.reLayout();
		this.toolbar && this.toolbar.reLayout();
	}

	public focusField(fieldName: string) {
		this.headerFieldsByName[fieldName].field.focus();
	}

	onResize(): void {
		if (!this.attachedToDom || this.getMainDomElement()[0].offsetWidth <= 0) return;
		this.relayoutHeader();
		this.toolbar && this.toolbar.reLayout();
		this.contentComponent && this.contentComponent.reLayout();
		this.leftHeaderField && this.leftHeaderField.field.onResize();
		this.rightHeaderField && this.rightHeaderField.field.onResize();
	}

	@executeWhenAttached(true)
	private relayoutHeader() {
		let availableHeaderContentWidth = this.$heading[0].offsetWidth - parseInt(this.$heading.css("padding-left")) - parseInt(this.$heading.css("padding-right"));
		if (this.title && this.titleNaturalWidth == 0) this.recalculateTitleNaturalWidth();
		if (Object.keys(this.headerFieldsByName).some(fieldName => !this.headerFieldsByName[fieldName].minimizedWidth)) this.calculateFieldWrapperSizes();

		let titleWidth = Math.floor(this.title ? this.titleNaturalWidth : 0);
		let iconWidth = (this.icon ? this.$icon[0].offsetWidth + parseInt(this.$icon.css("margin-right")) : 0);
		let minSpacerWidth = parseInt(this.$headingSpacer.css("flex-basis"));
		let buttonContainerWidth = this.$buttonContainer[0].offsetWidth;
		let specialButtonContainerWidth = this.$specialButtonContainer[0].offsetWidth;

		let minAllExpandedWidth =
			// 1 // the width of the title may be no integer
			-1
			+ iconWidth
			+ titleWidth
			+ minSpacerWidth
			+ buttonContainerWidth
			+ specialButtonContainerWidth
			+ Object.keys(this.headerFieldsByName)
				.map(fieldName => {
					let headerField = this.headerFieldsByName[fieldName];
					return headerField.minExpandedWidth;
				})
				.reduce((totalWidth, fieldWidth) => (totalWidth + fieldWidth), 0);

		if (this.numberOfVisibleHeaderFields() == 2) {
			let firstMinimizedField = this.leftComponentFirstMinimized ? this.leftHeaderField : this.rightHeaderField;
			let alwaysMaximizedField = this.leftComponentFirstMinimized ? this.rightHeaderField : this.leftHeaderField;
			let minFirstMinimizedWidth =
				1 // the width of the title may be no integer
				+ iconWidth
				+ titleWidth
				+ minSpacerWidth
				+ buttonContainerWidth
				+ specialButtonContainerWidth
				+ firstMinimizedField.minimizedWidth
				+ alwaysMaximizedField.minExpandedWidthWithIcon;
			let minFirstMinimizedAndHiddenTitleWidth = minFirstMinimizedWidth - titleWidth;

			this.setHeaderFieldMinimized(alwaysMaximizedField, false);

			if (availableHeaderContentWidth >= minAllExpandedWidth) {
				this.$title.removeClass("hidden").css("width", "");
				this.$heading.removeClass("has-minimized-header-component");
				let availableAdditionalSpace = availableHeaderContentWidth - minAllExpandedWidth;

				let minMaxFieldWidthDeltaSum = Object.keys(this.headerFieldsByName)
					.map(fieldName => {
						let headerField = this.headerFieldsByName[fieldName];
						return headerField.config.maxWidth - headerField.config.minWidth;
					})
					.reduce((totalWidth, fieldWidth) => (totalWidth + fieldWidth), 0);

				Object.keys(this.headerFieldsByName).forEach(fieldName => {
					let headerField = this.headerFieldsByName[fieldName];
					this.setHeaderFieldMinimized(headerField, false);
					let newFieldWidth = Math.min(
						headerField.config.minWidth + availableAdditionalSpace * ((headerField.config.maxWidth - headerField.config.minWidth) / minMaxFieldWidthDeltaSum),
						headerField.config.maxWidth
					);
					headerField.$fieldWrapper.css({
						width: newFieldWidth,
					});
				});
			} else if (availableHeaderContentWidth >= minFirstMinimizedWidth) {
				this.$title.removeClass("hidden").css("width", "");
				this.$heading.addClass("has-minimized-header-component");
				this.setHeaderFieldMinimized(firstMinimizedField, true);
				let availableAdditionalSpace = availableHeaderContentWidth - minFirstMinimizedWidth;
				let newMaximizedFieldWidth = Math.min(alwaysMaximizedField.config.minWidth + availableAdditionalSpace, alwaysMaximizedField.config.maxWidth);
				alwaysMaximizedField.$fieldWrapper.css({
					width: newMaximizedFieldWidth,
				});
			} else if (availableHeaderContentWidth >= minFirstMinimizedAndHiddenTitleWidth + 30 /* less does not make sense for title */) {
				this.$title.removeClass("hidden");
				this.$heading.addClass("has-minimized-header-component");
				this.setHeaderFieldMinimized(firstMinimizedField, true);
				alwaysMaximizedField.$fieldWrapper.css({
					width: alwaysMaximizedField.config.minWidth,
				});
				this.$title.outerWidth(this.titleNaturalWidth - (minFirstMinimizedWidth - availableHeaderContentWidth));
			} else {
				this.$title.addClass("hidden");
				this.$heading.addClass("has-minimized-header-component");
				this.setHeaderFieldMinimized(firstMinimizedField, true);
				alwaysMaximizedField.$fieldWrapper.css({
					width: alwaysMaximizedField.config.minWidth - (minFirstMinimizedAndHiddenTitleWidth - availableHeaderContentWidth),
				});
			}
		} else if (this.numberOfVisibleHeaderFields() == 1) {
			this.$heading.removeClass("has-minimized-header-component");
			let headerField = Object.keys(this.headerFieldsByName).map(fieldName => this.headerFieldsByName[fieldName])[0];
			this.setHeaderFieldMinimized(headerField, false);

			if (availableHeaderContentWidth >= minAllExpandedWidth) {
				this.$title.removeClass("hidden").css("width", "");
				let availableAdditionalSpace = availableHeaderContentWidth - minAllExpandedWidth;
				Object.keys(this.headerFieldsByName).forEach(fieldName => {
					let headerField = this.headerFieldsByName[fieldName];
					let newFieldWidth = Math.min(
						headerField.config.minWidth + availableAdditionalSpace,
						headerField.config.maxWidth
					);
					headerField.$fieldWrapper.css({
						width: newFieldWidth,
					});
				});
			} else if (availableHeaderContentWidth >= minAllExpandedWidth - this.titleNaturalWidth + 30 /* less does not make sense for title */) {
				this.$title.removeClass("hidden");
				headerField.$fieldWrapper.css({
					width: headerField.config.minWidth,
				});
				this.$title.outerWidth(this.titleNaturalWidth - (minAllExpandedWidth - availableHeaderContentWidth));
			} else {
				this.$title.addClass("hidden");
				let widthLessThanNeeded = availableHeaderContentWidth - minAllExpandedWidth + this.titleNaturalWidth;
				headerField.$fieldWrapper.css({
					width: headerField.config.minWidth + widthLessThanNeeded,
				});
			}
		} else {
			this.$heading.removeClass("has-minimized-header-component");
			this.$title.removeClass("hidden");
			let availableAdditionalSpace = availableHeaderContentWidth - minAllExpandedWidth;
			this.$title.outerWidth(this.titleNaturalWidth + availableAdditionalSpace);
		}
	};

	private numberOfVisibleHeaderFields() {
		return Object.keys(this.headerFieldsByName).filter(fieldName => this.headerFieldsByName[fieldName].field.isVisible()).length;
	}

	setFieldVisible(fieldName: string, visible: boolean): void {
		let field = this.headerFieldsByName[fieldName];
		if (!field) {
			this.logger.error("Cannot find field: " + fieldName);
			return;
		}
		field.field.setVisible(visible);
	}

	invokeOnField(fieldName: string, fieldCommand: any): void {
		let field = this.headerFieldsByName[fieldName];
		if (!field) {
			this.logger.error("Cannot find field: " + fieldName);
			return;
		}
		SubCommandExecutor.executeCommand(field, fieldCommand);
	}

	public destroy(): void {
		Object.keys(this.headerFieldsByName).forEach(fieldName => this.headerFieldsByName[fieldName].field.destroy());
		this.contentComponent && this.contentComponent.destroy();
		this.toolbar && this.toolbar.destroy();
		this.$panel.remove(); // may be currently attached to document.body (maximized)
		this.dropDown.destroy();
	}

	public static isDraggablePanelHeadingElement(target: HTMLElement): boolean {
		let heading = target.closest('.panel-heading');
		if (heading == null) {
			return false;
		}
		return target.classList.contains('panel-heading')
			|| target.classList.contains('panel-icon')
			|| target.classList.contains('panel-title')
			|| target.classList.contains('panel-heading-spacer');
	}

	public static createSpecialToolButtonConfig(buttonType: SpecialToolButtonType, iconSize: number) {
		if (buttonType === SpecialToolButtonType.CLOSE) {
			return createUiToolButtonConfig(buttonType, createSimpleTextCellTemplate("icon", iconSize, null), {"icon": StaticIcons.CLOSE});
		} else if (buttonType === SpecialToolButtonType.MAXIMIZE) {
			return createUiToolButtonConfig(buttonType, createSimpleTextCellTemplate("icon", iconSize, null), {"icon": StaticIcons.MAXIMIZE});
		} else if (buttonType === SpecialToolButtonType.MINIMIZE) {
			return createUiToolButtonConfig(buttonType, createSimpleTextCellTemplate("icon", iconSize, null), {"icon": StaticIcons.MINIMIZE});
		} else if (buttonType === SpecialToolButtonType.RESTORE) {
			return createUiToolButtonConfig(buttonType, createSimpleTextCellTemplate("icon", iconSize, null), {"icon": StaticIcons.RESTORE});
		}
	}
}

TeamAppsUiComponentRegistry.registerComponentClass("UiPanel", UiPanel);